import React from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  useMediaQuery,
  useTheme,
  Typography,
  Box,
  Stack,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DescriptionIcon from "@mui/icons-material/Description";
import LanguageIcon from "@mui/icons-material/Language";

const COLORS = {
  background: "#FFFFFF",
  surface: "#F7F9FC",
  surfaceHover: "#EEF2F7",
  border: "#E5E9F0",
  primary: "#10B981", // Keep green only for outline/border
  text: "#000000",
  textSecondary: "#6B7280",
  iconBg: "#F3F4F6",
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    background: COLORS.background,
    color: COLORS.text,
    borderRadius: 28,
    border: `1px solid ${COLORS.border}`,
    overflow: "hidden",
    boxShadow: "0 24px 80px rgba(0,0,0,0.08)",

    [theme.breakpoints.down("sm")]: {
      borderRadius: 0,
      margin: 0,
      maxWidth: "100%",
      width: "100%",
      height: "100%",
    },
  },
}));

const ResourceCard = styled(Box)({
  background: COLORS.surface,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 20,
  padding: 18,
  cursor: "pointer",
  transition: "all .25s ease",
  display: "flex",
  alignItems: "center",
  gap: 16,

  "&:hover": {
    background: COLORS.surfaceHover,
    borderColor: COLORS.primary,
    transform: "translateY(-2px)",
    boxShadow: "0 10px 30px rgba(16,185,129,0.08)",
  },
});

const IconContainer = ({ children }) => (
  <Box
    sx={{
      width: 56,
      height: 56,
      borderRadius: 3,
      background: COLORS.iconBg,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }}
  >
    {children}
  </Box>
);

const resources = [
  {
    title: "Financial Wellness Monitor",
    description: "Assess and track your financial wellbeing.",
    icon: <LanguageIcon sx={{ color: "#6B7280", fontSize: 28 }} />,
    url: "https://www.oldmutual.com.gh/personal/old-mutual-financial-wellness-monitor/",
  },
  {
    title: "Ghana 2025 Report",
    description: "Download the latest financial wellness report.",
    icon: <DescriptionIcon sx={{ color: "#6B7280", fontSize: 28 }} />,
    url: "https://www.oldmutual.com.gh/v3/assets/bltdd392fd32dda3ce8/bltb798543889d81bd6/6a042436e0b0444de488a8e0/Ghana_2025_Report.pdf",
  },
];

const FinancialWellnessModal = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const openLink = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
    >
      <DialogContent
        sx={{
          p: 0,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3,
            pt: isMobile ? 6 : 4,
            pb: 3,
            position: "relative",
            borderBottom: `1px solid ${COLORS.border}`,
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 16,
              top: 16,
              width: 42,
              height: 42,
              bgcolor: COLORS.surface,
              border: `1px solid ${COLORS.border}`,
              color: COLORS.text,

              "&:hover": {
                bgcolor: COLORS.surfaceHover,
                borderColor: COLORS.primary,
              },
            }}
          >
            <CloseIcon />
          </IconButton>

          <Typography
            variant={isMobile ? "h5" : "h4"}
            fontWeight={800}
            sx={{
              mb: 1,
              color: COLORS.text,
            }}
          >
            Financial Wellness
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: COLORS.textSecondary,
              maxWidth: 500,
              lineHeight: 1.7,
            }}
          >
            Explore trusted tools and reports to better understand, measure, and
            improve your financial wellbeing.
          </Typography>
        </Box>

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: 3,
            py: 3,
          }}
        >
          <Stack spacing={2}>
            {resources.map((resource) => (
              <ResourceCard
                key={resource.title}
                onClick={() => openLink(resource.url)}
              >
                <IconContainer>{resource.icon}</IconContainer>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{
                      color: COLORS.text,
                      mb: 0.5,
                    }}
                  >
                    {resource.title}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: COLORS.textSecondary,
                      lineHeight: 1.5,
                    }}
                  >
                    {resource.description}
                  </Typography>
                </Box>

                <OpenInNewIcon
                  sx={{
                    color: "#9CA3AF",
                    fontSize: 22,
                    flexShrink: 0,
                  }}
                />
              </ResourceCard>
            ))}
          </Stack>
        </Box>
      </DialogContent>
    </StyledDialog>
  );
};

export default FinancialWellnessModal;
